import { styled } from "@mui/material/styles";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";
import Check from "@mui/icons-material/Check";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import { AdhocKycStep } from "src/store/features/api/adhocKyc";
import { ADHOC_KYC_MINT, ADHOC_KYC_NAVY } from "./AdhocKycShell";

// Parameterised copy of the onboarding `Stepper.tsx` look (Qonto connector +
// dot/tick icons) — that component reads its labels from the onboarding and
// user slices, neither of which exists in token mode.

export const ADHOC_KYC_STEP_LABELS = ["Consent", "Your details", "ID check", "Questions"];

const ID_CHECK_INDEX = 2;

/** Position of a server-derived step on the four-label rail; `done` sits past the end. */
export const adhocKycStepIndex = (step: AdhocKycStep): number => {
  switch (step) {
    case "consent":
      return 0;
    case "details":
    case "update":
      return 1;
    case "onfido":
      return ID_CHECK_INDEX;
    case "questions":
      return 3;
    default:
      return ADHOC_KYC_STEP_LABELS.length;
  }
};

const QontoConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: { borderColor: ADHOC_KYC_MINT },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { borderColor: ADHOC_KYC_MINT },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: "#eaeaf0",
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled("div")<{ ownerState: { active?: boolean } }>(
  ({ ownerState }) => ({
    color: "#eaeaf0",
    display: "flex",
    height: 22,
    alignItems: "center",
    ...(ownerState.active && { color: ADHOC_KYC_NAVY }),
    "& .QontoStepIcon-completedIcon": {
      color: ADHOC_KYC_MINT,
      zIndex: 1,
      fontSize: 18,
    },
    "& .QontoStepIcon-circle": {
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "currentColor",
    },
  }),
);

function QontoStepIcon(props: StepIconProps): JSX.Element {
  const { active, completed, className } = props;
  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

export interface AdhocKycStepperProps {
  step: AdhocKycStep;
  /**
   * The stale branch (details updated → Onfido) is the only one that visits
   * "ID check". On the happy path the step is shown greyed as "Not needed"
   * once the customer is past it, rather than ticked as if it had run.
   */
  idCheckRequired: boolean;
}

export default function AdhocKycStepper({
  step,
  idCheckRequired,
}: AdhocKycStepperProps): JSX.Element {
  const active = adhocKycStepIndex(step);
  const idCheckSkipped = active > ID_CHECK_INDEX && !idCheckRequired;
  return (
    <Stepper
      alternativeLabel
      activeStep={active}
      connector={<QontoConnector />}
      data-testid="adhoc-kyc-stepper"
      sx={{ mb: 4 }}>
      {ADHOC_KYC_STEP_LABELS.map((label, index) => {
        const skipped = index === ID_CHECK_INDEX && idCheckSkipped;
        return (
          <Step
            key={label}
            completed={skipped ? false : undefined}
            sx={skipped ? { opacity: 0.5 } : undefined}
            data-testid={skipped ? "adhoc-kyc-step-skipped" : undefined}>
            <StepLabel
              StepIconComponent={QontoStepIcon}
              optional={
                skipped ? <Typography variant="caption">Not needed</Typography> : undefined
              }>
              {label}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
}
